import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Download, ImageIcon, Loader2, Lock } from "lucide-react";
import MiniHeader from "@/components/site/MiniHeader";
import FilmSection from "@/components/site/FilmSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  ["Family contact", "name"], ["Email", "email"], ["Phone", "phone"], ["Service date", "service_date"],
  ["Photo estimate", "photo_count"], ["Style", "style"], ["Music", "music_preference"],
];

export default function OwnerPhotosPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios.get(`${API}/owner/${token}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [token]);

  if (notFound) {
    return (
      <div className="min-h-screen"><MiniHeader />
        <div data-testid="owner-invalid-link" className="max-w-xl mx-auto px-6 py-32 text-center">
          <h1 className="serif text-4xl font-light text-[#F5F0E8]">This link isn&apos;t valid</h1>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#C5A55A]" /></div>;

  const base = `${API}/owner/${token}`;
  const setPayment = async (status) => {
    try {
      const { data: payment } = await axios.post(`${base}/payment`, { status });
      setData((d) => ({ ...d, payment }));
    } catch {
      toast.error("Could not update payment. Please try again.");
    }
  };
  return (
    <div data-testid="owner-photos-page" className="min-h-screen">
      <MiniHeader right={
        <div className="flex items-center gap-3 text-xs">
          <a href="/admin" data-testid="owner-all-families-link" className="text-[#A0988E] hover:text-[#F5F0E8] whitespace-nowrap">← All families</a>
          <span className="hidden sm:inline-flex items-center gap-2 text-[#A0988E]"><Lock size={13} className="text-[#D4AF37]" /> Owner view</span>
        </div>
      } />
      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-14">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <div className="overline mb-3">Tribute request</div>
            <h1 data-testid="owner-loved-one-name" className="serif text-4xl sm:text-5xl font-light text-[#F5F0E8]">{data.loved_one_name}</h1>
            <p className="text-sm text-[#A0988E] mt-2">Received {new Date(data.created_at).toLocaleString()}</p>
          </div>
          {data.photos.length > 0 && (
            <a data-testid="owner-download-zip" href={`${base}/zip`} className="btn-primary">
              <Download size={16} /> Download all ({data.photos.length})
            </a>
          )}
        </div>

        <div className="mt-8 card-surface p-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-4">
          <div className="sm:col-span-2 lg:col-span-4 flex items-center justify-between gap-4 flex-wrap pb-4 border-b border-[rgba(197,165,90,0.15)]">
            <div>
              <div className="text-[11px] tracking-widest uppercase text-[#C5A55A]/80">Cash App payment · ${data.payment.total.toFixed(2)} to {data.payment.cashtag}</div>
              <div data-testid="owner-payment-status" className="text-[#F5F0E8] mt-1">
                {{ unpaid: "Not paid yet", reported: "Family says it's sent — check Cash App", confirmed: "Received" }[data.payment.status]}
              </div>
            </div>
            {data.payment.status !== "confirmed" ? (
              <button data-testid="owner-confirm-payment" onClick={() => setPayment("confirmed")} className="btn-primary text-sm">Mark as received</button>
            ) : (
              <button data-testid="owner-undo-payment" onClick={() => setPayment("unpaid")} className="btn-ghost text-sm">Undo</button>
            )}
          </div>
          {FIELDS.filter(([, k]) => data[k]).map(([label, k]) => (
            <div key={k}>
              <div className="text-[11px] tracking-widest uppercase text-[#C5A55A]/80">{label}</div>
              <div data-testid={`owner-field-${k}`} className="text-[#F5F0E8] mt-1 break-words">{data[k]}</div>
            </div>
          ))}
          {data.message && (
            <div className="sm:col-span-2 lg:col-span-4">
              <div className="text-[11px] tracking-widest uppercase text-[#C5A55A]/80">Message</div>
              <div data-testid="owner-field-message" className="text-[#F5F0E8]/90 mt-1 spectral whitespace-pre-wrap">{data.message}</div>
            </div>
          )}
        </div>

        <FilmSection token={token} film={data.film} familyEmail={data.email} onChange={(film) => setData((d) => ({ ...d, film }))} />

        <h2 className="serif text-2xl text-[#F5F0E8] mt-12">Photos <span className="text-[#A0988E] text-lg">({data.photos.length})</span></h2>
        {data.photos.length === 0 ? (
          <p data-testid="owner-no-photos" className="mt-4 text-[#A0988E] spectral italic">The family hasn&apos;t uploaded photos yet. You&apos;ll get an email when they do.</p>
        ) : (
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {data.photos.map((p) => (
              <a key={p.id} data-testid={`owner-photo-${p.id}`} href={`${base}/photos/${p.id}/file`} title={`Download ${p.original_filename}`}
                 className="group relative aspect-square rounded-xl overflow-hidden border border-[rgba(197,165,90,0.2)] bg-[#141311]">
                {p.has_thumb ? (
                  <img src={`${base}/photos/${p.id}/thumb`} alt={p.original_filename} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-2 p-2"><ImageIcon size={22} className="text-[#C5A55A]" /><span className="text-[10px] text-[#A0988E] break-all text-center">{p.original_filename}</span></div>
                )}
                <span className="absolute bottom-2 right-2 h-8 w-8 inline-flex items-center justify-center rounded-full bg-[rgba(11,10,9,0.75)] border border-[rgba(212,175,55,0.3)]">
                  <Download size={13} className="text-[#F5F0E8]" />
                </span>
              </a>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
